/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState } from 'react';
import {
  Button, Form, Row, Col, Badge, Spinner,
} from 'react-bootstrap';
import dayjs from 'dayjs';
import BookingService from '../../../Services/BookingService';
import PartnersService from '../../../Services/PartnersService';
import { get } from '../../../Services/API';
import BookingReferenceSearch from '../../Payments/Components/BookingReferenceSearch';
import FinalInvoiceBlockedAlert from '../../../Components/TripClaims/FinalInvoiceBlockedAlert';

const BASES = [
  {
    key: 'bookings', label: 'Bookings', icon: 'fa-list-check', desc: 'One or more bookings',
  },
  {
    key: 'event', label: 'Event', icon: 'fa-calendar-day', desc: 'All bookings in an event',
  },
  {
    key: 'partner', label: 'Partner', icon: 'fa-handshake', desc: 'A partner over a period',
  },
];

// Resolves the set of bookings to invoice via one of three bases and reports
// it upward. By-partner takes a date range so it fits monthly billing cycles.
function BillingBasisPicker({ onResolved }) {
  const [basis, setBasis] = useState('bookings');
  const [loading, setLoading] = useState(false);
  // The bookings this basis resolved to, kept so the picker can say up front
  // which of them cannot be finally invoiced yet — at month-end that answer
  // is wanted when the PERIOD is chosen, not three steps later when "Create
  // Számla" is already greyed out and naming nothing.
  const [resolved, setResolved] = useState([]);

  // by bookings
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState([]);

  // by bookings — bulk paste
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteLoading, setPasteLoading] = useState(false);
  const [pasteResult, setPasteResult] = useState(null);

  // by event
  const [eventNames, setEventNames] = useState([]);
  const [eventName, setEventName] = useState('');

  // by partner — default to the last 30 days so a "Load" never pulls the
  // partner's entire booking history by accident (monthly billing is periodic).
  const [partners, setPartners] = useState([]);
  const [partnerId, setPartnerId] = useState('');
  const [from, setFrom] = useState(dayjs().subtract(30, 'day').format('YYYY-MM-DD'));
  const [to, setTo] = useState(dayjs().format('YYYY-MM-DD'));

  useEffect(() => {
    BookingService.getEventNames().then(setEventNames).catch(() => setEventNames([]));
    PartnersService.fetchPartners()
      .then((res) => setPartners(res?.data?.data || []))
      .catch(() => setPartners([]));
  }, []);

  /** Report a resolved basis upward, and keep it for the warning below. */
  const resolve = (result) => {
    setResolved(result.bookings || []);
    onResolved(result);
  };

  const setPickedAndReport = (next) => {
    setPicked(next);
    resolve({ bookings: next, label: 'Selected bookings', partner: null });
  };

  const addPicked = (booking) => {
    setSearch('');
    if (picked.some((b) => b._id === booking._id)) return;
    setPickedAndReport([...picked, booking]);
  };

  // Resolve a pasted list of references (whitespace/comma/newline separated) in
  // one go and add every match — faster than searching one at a time.
  const addFromPaste = async () => {
    const refs = [...new Set(pasteText.split(/[\s,;]+/).map((t) => t.trim()).filter(Boolean))];
    if (refs.length === 0) return;
    setPasteLoading(true);
    setPasteResult(null);
    try {
      const looked = await Promise.all(refs.map(async (ref) => {
        try {
          const r = await get('/bookings', { filters: { booking_reference: ref } });
          return r.data?.data?.documents?.[0] || null;
        } catch {
          return null;
        }
      }));
      const merged = [...picked];
      looked.filter(Boolean).forEach((doc) => {
        if (!merged.some((b) => b._id === doc._id)) merged.push(doc);
      });
      const notFound = refs.filter((ref, i) => !looked[i]);
      setPickedAndReport(merged);
      setPasteText('');
      setPasteResult({ added: merged.length - picked.length, notFound });
    } finally {
      setPasteLoading(false);
    }
  };
  const removePicked = (id) => {
    setPickedAndReport(picked.filter((b) => b._id !== id));
  };

  const loadEvent = async (name) => {
    setEventName(name);
    if (!name) { resolve({ bookings: [], label: '', partner: null }); return; }
    setLoading(true);
    try {
      const res = await BookingService.getEventBookings(name);
      resolve({ bookings: res?.bookings || [], label: `Event: ${name}`, partner: null });
    } finally {
      setLoading(false);
    }
  };

  const loadPartner = async () => {
    if (!partnerId) return;
    setLoading(true);
    try {
      const filters = { partners: [partnerId] };
      if (from && to) {
        filters.date = [
          dayjs(from).startOf('day').toISOString(),
          dayjs(to).endOf('day').toISOString(),
        ];
      }
      const res = await get('/bookings', { filters, pageSize: 200 });
      const partner = partners.find((p) => p.Id === partnerId) || null;
      const period = from && to ? ` (${from} → ${to})` : '';
      resolve({
        bookings: res.data?.data?.documents || [],
        label: `Partner: ${partner?.name || partnerId}${period}`,
        partner,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="d-flex gap-2 mb-3 flex-wrap">
        {BASES.map((b) => {
          const active = basis === b.key;
          return (
            <button
              type="button"
              key={b.key}
              onClick={() => setBasis(b.key)}
              className={`flex-fill text-start border rounded-3 p-2 ${active ? 'border-primary bg-primary-subtle' : 'border-light-subtle bg-white'}`}
              style={{ minWidth: 150, cursor: 'pointer' }}
            >
              <div className="d-flex align-items-center gap-2">
                <i className={`fa ${b.icon} ${active ? 'text-primary' : 'text-muted'}`} />
                <span className="fw-semibold">{b.label}</span>
              </div>
              <div className="small text-muted">{b.desc}</div>
            </button>
          );
        })}
      </div>

      {loading && (
        <span className="text-muted small">
          <Spinner size="sm" animation="border" className="me-2" />
          Loading…
        </span>
      )}

      {basis === 'bookings' && (
        <div>
          <div className="d-flex justify-content-between align-items-end">
            <div className="flex-grow-1">
              <BookingReferenceSearch
                value={search}
                onChange={setSearch}
                // selected={null} keeps the box in "add another" mode: each pick
                // clears it instead of locking the input behind an ×.
                selected={null}
                onResolve={(booking, { reason }) => reason === 'match' && booking && addPicked(booking)}
                label="Add bookings by reference, name, email or phone"
              />
            </div>
            <Button
              variant="link"
              size="sm"
              className="ms-2 text-nowrap"
              onClick={() => setPasteOpen((o) => !o)}
            >
              <i className="fa fa-list me-1" />
              Paste a list
            </Button>
          </div>

          {pasteOpen && (
            <div className="mt-2 p-2 border rounded bg-light">
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Paste booking references separated by spaces, commas or new lines…"
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
              />
              <div className="d-flex align-items-center gap-2 mt-2">
                <Button size="sm" onClick={addFromPaste} disabled={pasteLoading || !pasteText.trim()}>
                  {pasteLoading ? 'Adding…' : 'Add all'}
                </Button>
                {pasteResult && (
                  <span className="small text-muted">
                    Added
                    {' '}
                    {pasteResult.added}
                    {pasteResult.notFound.length > 0 && (
                      <>
                        {' · not found: '}
                        {pasteResult.notFound.join(', ')}
                      </>
                    )}
                  </span>
                )}
              </div>
            </div>
          )}

          {picked.length > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3 mb-1">
              <span className="small text-muted">
                {picked.length}
                {' '}
                booking
                {picked.length === 1 ? '' : 's'}
                {' '}
                selected
              </span>
              <Button variant="link" size="sm" className="text-muted p-0" onClick={() => setPickedAndReport([])}>
                Clear all
              </Button>
            </div>
          )}
          <div className="d-flex flex-wrap gap-2">
            {picked.map((b) => (
              <Badge key={b._id} bg="secondary" className="d-flex align-items-center gap-1">
                {b.booking_reference || b._id}
                <button
                  type="button"
                  className="btn-close btn-close-white btn-sm ms-1"
                  aria-label="Remove"
                  onClick={() => removePicked(b._id)}
                />
              </Badge>
            ))}
          </div>
        </div>
      )}

      {basis === 'event' && (
        <Form.Group>
          <Form.Label>Event</Form.Label>
          <Form.Select value={eventName} onChange={(e) => loadEvent(e.target.value)}>
            <option value="">Select an event…</option>
            {eventNames.map((name) => <option key={name} value={name}>{name}</option>)}
          </Form.Select>
        </Form.Group>
      )}

      {basis === 'partner' && (
        <Row className="g-2 align-items-end">
          <Form.Group as={Col} md={5}>
            <Form.Label>Partner</Form.Label>
            <Form.Select value={partnerId} onChange={(e) => setPartnerId(e.target.value)}>
              <option value="">Select a partner…</option>
              {partners.map((p) => <option key={p.Id} value={p.Id}>{p.name}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group as={Col} md={3}>
            <Form.Label>From</Form.Label>
            <Form.Control type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Form.Group>
          <Form.Group as={Col} md={3}>
            <Form.Label>To</Form.Label>
            <Form.Control type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Form.Group>
          <Col md={1}>
            <Button onClick={loadPartner} disabled={!partnerId || !from || !to}>Load</Button>
          </Col>
        </Row>
      )}

      {/* Which of the bookings just loaded stand between this basis and a
          final Számla — the same list the server's refusal is worded from. */}
      <FinalInvoiceBlockedAlert bookings={resolved} className="mt-3" />
    </div>
  );
}

export default BillingBasisPicker;
